import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { cn } from '../lib/utils';
import { Search, MapPin, User, Tag, Loader2 } from 'lucide-react';

/**
 * AutocompleteInput - Reusable autocomplete component
 * 
 * Usage:
 * <AutocompleteInput
 *   value={searchQuery}
 *   onChange={setSearchQuery}
 *   onSelect={(item) => handleSelect(item)}
 *   fetchSuggestions={async (q) => await autocompleteApi.talents(q)}
 *   placeholder="Search talents..."
 *   type="talent" // or "location"
 * />
 */

const AutocompleteInput = ({
  value,
  onChange,
  onSelect,
  fetchSuggestions,
  placeholder = "Search...",
  type = "talent", // talent | location
  className = "",
  disabled = false,
  debounceMs = 300,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Fetch suggestions with debounce
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!value || value.length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await fetchSuggestions(value);
        const data = response.data || response;
        setSuggestions(data.suggestions || []);
        setIsOpen(data.suggestions?.length > 0);
      } catch (error) {
        console.error('Autocomplete error:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [value, fetchSuggestions, debounceMs]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
          handleSelect(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleSelect = (item) => {
    onChange(item.value);
    onSelect?.(item);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const getIcon = (itemType) => {
    switch (itemType) {
      case 'category':
        return <Tag className="h-4 w-4 text-primary" />;
      case 'talent':
        return <User className="h-4 w-4 text-blue-400" />;
      case 'city':
      case 'venue_city':
      case 'user_location':
        return <MapPin className="h-4 w-4 text-green-400" />;
      default:
        return <Search className="h-4 w-4 text-muted-foreground" />;
    }
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="pl-10 pr-10 rounded-xl"
          data-testid={`autocomplete-${type}-input`}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
        )}
      </div>

      {/* Dropdown */}
      {isOpen && suggestions.length > 0 && (
        <div 
          className="absolute z-50 w-full mt-1 bg-background border border-border rounded-xl shadow-lg overflow-hidden"
          data-testid={`autocomplete-${type}-dropdown`}
        >
          <ul className="py-1 max-h-60 overflow-y-auto">
            {suggestions.map((item, index) => (
              <li
                key={`${item.type}-${item.value}-${index}`}
                onClick={() => handleSelect(item)}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={cn(
                  "flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors",
                  highlightedIndex === index 
                    ? "bg-primary/10 text-foreground" 
                    : "hover:bg-muted/50"
                )}
                data-testid={`autocomplete-suggestion-${index}`}
              >
                {getIcon(item.type)}
                <div className="flex-1 min-w-0">
                  <span className="block truncate text-sm">{item.label}</span>
                  {item.type === 'talent' && (
                    <span className="text-xs text-muted-foreground">Talent</span>
                  )}
                </div>
                <span className="text-xs text-muted-foreground capitalize">
                  {item.type.replace('_', ' ')}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AutocompleteInput;
